import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { Envelope, X, Plus } from "@phosphor-icons/react";

export default function Reports() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showIngest, setShowIngest] = useState(false);
  const [ingestForm, setIngestForm] = useState({ subject: "", sender: "", body: "" });
  const [ingesting, setIngesting] = useState(false);
  const [ingestError, setIngestError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const q = filter !== "all" ? `?status=${filter}` : "";
      const { data } = await api.get(`/reports${q}`);
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const ingest = async (e) => {
    e.preventDefault();
    setIngesting(true); setIngestError("");
    try {
      await api.post("/reports", {
        source: "email",
        subject: ingestForm.subject,
        sender: ingestForm.sender || null,
        body: ingestForm.body,
      });
      setShowIngest(false);
      setIngestForm({ subject: "", sender: "", body: "" });
      await load();
    } catch (e) {
      setIngestError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setIngesting(false); }
  };

  return (
    <Layout>
      <PageHeader
        title="Inbox / reports"
        subtitle="Parsed backup notifications (AI-classified)"
        actions={
          <>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="reports-filter-select"
              className="bg-background-deep border border-border text-sm rounded-md px-3 py-1.5 focus:outline-none focus:border-brand-cyan">
              <option value="all">All statuses</option>
              <option value="healthy">Healthy</option>
              <option value="warning">Warning</option>
              <option value="failure">Failure</option>
              <option value="unknown">Unknown</option>
            </select>
            <button onClick={() => setShowIngest(true)} data-testid="reports-ingest-btn"
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">
              <Plus size={14} weight="bold" /> Ingest email
            </button>
          </>
        }
      />
      <div className="p-6">
        {loading ? <div className="text-sm text-slate-500">loading…</div>
         : items.length === 0 ? (
          <div className="sw-card border-dashed p-16 text-center">
            <Envelope size={36} weight="duotone" className="text-brand-cyan mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold tracking-tight mb-1">No reports yet</h3>
            <p className="text-sm text-slate-400 mb-4">Ingest a backup email manually, or configure IMAP polling in Integrations.</p>
            <button onClick={() => setShowIngest(true)} data-testid="reports-empty-cta"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">
              <Plus size={14} weight="bold" /> Ingest a sample email
            </button>
          </div>
        ) : (
          <div className="sw-card overflow-hidden">
            <table className="w-full text-sm" data-testid="reports-table">
              <thead className="bg-background-deep/60">
                <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                  <th className="text-left py-3 px-4">When</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Subject</th>
                  <th className="text-left py-3 px-4">From</th>
                  <th className="text-left py-3 px-4 w-28">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} onClick={() => setSelected(r)} data-testid={`report-row-${r.id}`}
                    className="border-t border-border hover:bg-white/5 cursor-pointer">
                    <td className="py-3 px-4 text-xs text-slate-400 whitespace-nowrap">
                      {new Date(r.occurred_at).toLocaleString()}
                    </td>
                    <td className="py-3 px-4"><StatusBadge status={r.status} /></td>
                    <td className="py-3 px-4 text-slate-100 truncate max-w-md">{r.subject || "—"}</td>
                    <td className="py-3 px-4 text-xs text-slate-400 truncate max-w-xs">{r.sender || "—"}</td>
                    <td className="py-3 px-4 text-xs text-slate-400">{(r.confidence * 100).toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/60 z-30" onClick={() => setSelected(null)}>
          <aside onClick={(e) => e.stopPropagation()}
            className="absolute right-0 top-0 h-full w-full max-w-2xl bg-background border-l border-border overflow-y-auto"
            data-testid="report-drawer">
            <div className="sticky top-0 bg-background-deep border-b border-border px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <StatusBadge status={selected.status} size="lg" />
                <span className="text-xs text-slate-400">{(selected.confidence * 100).toFixed(0)}% confidence</span>
              </div>
              <button onClick={() => setSelected(null)} data-testid="report-drawer-close"
                className="p-1.5 text-slate-300 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">Subject</div>
                <div className="text-base text-white font-display font-semibold">{selected.subject || "—"}</div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Info label="From"><span className="font-mono text-xs break-all">{selected.sender || "—"}</span></Info>
                <Info label="When"><span className="text-xs">{new Date(selected.occurred_at).toLocaleString()}</span></Info>
                <Info label="Source"><span className="text-xs">{selected.source}</span></Info>
                <Info label="Detected jobs"><span className="text-xs">{(selected.detected_jobs || []).join(", ") || "—"}</span></Info>
              </div>
              {selected.summary && (
                <div className="border border-brand-cyan/30 rounded p-3 bg-brand-cyan/5">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-cyan mb-1">AI summary</div>
                  <div className="text-sm text-slate-100">{selected.summary}</div>
                </div>
              )}
              {selected.body && (
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">Email body</div>
                  <pre className="font-mono text-xs whitespace-pre-wrap text-slate-200 bg-background-deep border border-border rounded p-3 max-h-96 overflow-auto">
                    {selected.body}
                  </pre>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}

      {showIngest && (
        <div className="fixed inset-0 bg-black/70 z-30 flex items-center justify-center p-6" onClick={() => setShowIngest(false)}>
          <form onSubmit={ingest} onClick={(e) => e.stopPropagation()}
            className="sw-card w-full max-w-2xl p-6 space-y-3" data-testid="ingest-form">
            <h3 className="font-display text-lg font-bold tracking-tight">Ingest backup email</h3>
            <p className="text-xs text-slate-400">We&apos;ll run it through your rules first, then Claude Sonnet 4.5.</p>
            <input required placeholder="Subject" value={ingestForm.subject} onChange={(e) => setIngestForm({ ...ingestForm, subject: e.target.value })}
              data-testid="ingest-subject-input"
              className="w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan" />
            <input placeholder="Sender (optional)" value={ingestForm.sender} onChange={(e) => setIngestForm({ ...ingestForm, sender: e.target.value })}
              data-testid="ingest-sender-input"
              className="w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan" />
            <textarea required rows={10} placeholder="Email body…" value={ingestForm.body} onChange={(e) => setIngestForm({ ...ingestForm, body: e.target.value })}
              data-testid="ingest-body-input"
              className="w-full bg-background-deep border border-border rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-cyan" />
            {ingestError && <div className="text-xs text-red-400">{ingestError}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={() => setShowIngest(false)} data-testid="ingest-cancel-btn"
                className="px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md">Cancel</button>
              <button type="submit" disabled={ingesting} data-testid="ingest-submit-btn"
                className="px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 disabled:opacity-60 rounded-md font-semibold">
                {ingesting ? "Classifying…" : "Ingest & classify"}
              </button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">{label}</div>
      <div className="text-slate-100">{children}</div>
    </div>
  );
}
